// Inline flag SVGs for the locale switcher (CSP blocks external images). ~20×14, rounded. Distinct
// flags are drawn; visual look-alikes (pan-Slavic tricolors) and any locale without a flag fall back
// to a clean uppercase code badge — perfectly legible in the dropdown.
import type { ReactNode } from 'react'

const box = { width: 20, height: 14, className: 'rounded-[2px]' } as const

// vertical tricolor (left→right)
const V = (a: string, b: string, c: string) => (
  <svg viewBox="0 0 3 2" {...box} aria-hidden="true">
    <rect width="1" height="2" x="0" fill={a} />
    <rect width="1" height="2" x="1" fill={b} />
    <rect width="1" height="2" x="2" fill={c} />
  </svg>
)
// horizontal tricolor (top→bottom)
const H = (a: string, b: string, c: string) => (
  <svg viewBox="0 0 3 3" {...box} aria-hidden="true">
    <rect width="3" height="1" y="0" fill={a} />
    <rect width="3" height="1" y="1" fill={b} />
    <rect width="3" height="1" y="2" fill={c} />
  </svg>
)
// horizontal bicolor
const H2 = (a: string, b: string) => (
  <svg viewBox="0 0 3 2" {...box} aria-hidden="true">
    <rect width="3" height="1" y="0" fill={a} />
    <rect width="3" height="1" y="1" fill={b} />
  </svg>
)
// Nordic cross: field + offset cross; optional inner cross (Norway)
const Nordic = (field: string, cross: string, inner?: string) => (
  <svg viewBox="0 0 16 11" {...box} aria-hidden="true">
    <rect width="16" height="11" fill={field} />
    <rect x="5" width="2" height="11" fill={cross} />
    <rect y="4.5" width="16" height="2" fill={cross} />
    {inner && (
      <>
        <rect x="5.5" width="1" height="11" fill={inner} />
        <rect y="5" width="16" height="1" fill={inner} />
      </>
    )}
  </svg>
)

const flagEN = (
  <svg viewBox="0 0 60 30" {...box} aria-hidden="true">
    <clipPath id="uk-s">
      <rect width="60" height="30" />
    </clipPath>
    <g clipPath="url(#uk-s)">
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 60,30M60,0 0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 60,30M60,0 0,30" clipPath="url(#uk-s)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
)

const flagEL = (
  <svg viewBox="0 0 27 18" {...box} aria-hidden="true">
    <rect width="27" height="18" fill="#0D5EAF" />
    {[3, 9, 15].map((y) => (
      <rect key={y} width="27" height="2" y={y} fill="#fff" />
    ))}
    <rect width="10" height="10" fill="#0D5EAF" />
    <rect x="4" width="2" height="10" fill="#fff" />
    <rect y="4" width="10" height="2" fill="#fff" />
  </svg>
)

/** Clean uppercase-code badge for any locale without a drawn flag. */
export const flagFallback = (code: string): ReactNode => (
  <span className="inline-flex h-[14px] min-w-[20px] items-center justify-center rounded-[2px] bg-surface-2 px-0.5 text-[8px] font-bold uppercase tracking-tight text-muted">
    {code}
  </span>
)

const flagTR = (
  <svg viewBox="0 0 30 20" {...box} aria-hidden="true">
    <rect width="30" height="20" fill="#E30A17" />
    <circle cx="11" cy="10" r="5" fill="#fff" />
    <circle cx="12.6" cy="10" r="4" fill="#E30A17" />
    <path
      d="M17.3,7.6 L17.86,9.23 L19.58,9.26 L18.2,10.29 L18.71,11.94 L17.3,10.95 L15.89,11.94 L16.4,10.29 L15.02,9.26 L16.74,9.23 Z"
      fill="#fff"
    />
  </svg>
)

export const FLAGS: Record<string, ReactNode> = {
  en: flagEN,
  tr: flagTR,
  uk: H2('#0057B7', '#FFDD00'),
  de: H('#000', '#D00', '#FFCE00'),
  fr: V('#0055A4', '#fff', '#EF4135'),
  es: (
    <svg viewBox="0 0 3 2" {...box} aria-hidden="true">
      <rect width="3" height="2" fill="#AA151B" />
      <rect width="3" height="1" y="0.5" fill="#F1BF00" />
    </svg>
  ),
  it: V('#009246', '#fff', '#CE2B37'),
  nl: H('#AE1C28', '#fff', '#21468B'),
  pl: H2('#fff', '#DC143C'),
  pt: (
    <svg viewBox="0 0 5 3" {...box} aria-hidden="true">
      <rect width="5" height="3" fill="#DA291C" />
      <rect width="2" height="3" fill="#046A38" />
    </svg>
  ),
  sv: Nordic('#006AA7', '#FECC00'),
  da: Nordic('#C60C30', '#fff'),
  fi: Nordic('#fff', '#003580'),
  no: Nordic('#EF2B2D', '#fff', '#002868'),
  el: flagEL,
  ro: V('#002B7F', '#FCD116', '#CE1126'),
  hu: H('#CD2A3E', '#fff', '#436F4D'),
  bg: H('#fff', '#00966E', '#D62612'),
  lt: H('#FDB913', '#006A44', '#C1272D'),
  lv: H('#9E3039', '#fff', '#9E3039'),
  et: H('#0072CE', '#000', '#fff'),
}
